import { useState } from 'react';
import { useChatbot } from '../';
import type { ChatbotTypes } from '../../core';

export const ChatbotTestUI = () => {
  const {
    injectScript,
    injectElement,
    mount,
    unmount,
    setMetadata,
    updateMetadata,
    show,
    hide,
    setOpen,
    onMessage,
    subscribeToEvent,
    unsubscribeFromEvent,
    fireEvent,
    trackEvent,
  } = useChatbot();

  const [messages, setMessages] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] =
    useState<ChatbotTypes.AppliedEventKey>('applied:chat:open');
  const [subscriptionId, setSubscriptionId] = useState<number | null>(null);

  const handleSetupListeners = () => {
    onMessage((msg) => setMessages((prev) => [...prev, `Message: ${JSON.stringify(msg)}`]));
    const id = subscribeToEvent(selectedEvent, (evt: ChatbotTypes.AppliedEventPayload<unknown>) =>
      setMessages((prev) => [...prev, `Event: ${JSON.stringify(evt)}`]),
    );
    setSubscriptionId(id);
  };

  const handleUnsubscribe = () => {
    if (subscriptionId !== null) {
      unsubscribeFromEvent(subscriptionId);
      setMessages((prev) => [...prev, `Unsubscribed from ${selectedEvent}`]);
      setSubscriptionId(null);
    }
  };

  const handleFireEvent = () => {
    fireEvent(selectedEvent, { custom: 'payload' });
  };

  const handleTrackEvent = () => {
    trackEvent('custom:event', 42, { source: 'test' });
  };

  const handleSetMetadata = () => {
    setMetadata({ context: { name: 'Initial Doe' } });
  };

  const handleUpdateMetadata = () => {
    updateMetadata({ context: { name: 'Updated Doe' } });
  };

  const eventOptions: Record<ChatbotTypes.AppliedEventKey, string> = {
    'applied:chat:open': 'Chat Open',
    'applied:chat:close': 'Chat Close',
    'applied:chat:toggle': 'Chat Toggle',
    'applied:agent:joined': 'Agent Joined',
    'applied:agent:left': 'Agent Left',
    'applied:conversation:end': 'Conversation End',
    'applied:conversation:message': 'Conversation Message',
    'applied:csat:submit': 'CSAT Submit',
    'applied:chat:timeout': 'Chat Timeout',
  };

  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto', fontFamily: 'sans-serif' }}>
      <h2>Chatbot Test UI</h2>
      <fieldset style={{ marginBottom: 20 }}>
        <legend>Initialization</legend>
        <button onClick={injectScript} style={{ marginRight: 8 }}>
          Inject Script
        </button>
        <button onClick={injectElement}>Inject Element</button>
      </fieldset>

      <fieldset style={{ marginBottom: 20 }}>
        <legend>Lifecycle</legend>
        <button onClick={() => mount()} style={{ marginRight: 8 }}>
          Mount
        </button>
        <button onClick={unmount}>Unmount</button>
      </fieldset>

      <fieldset style={{ marginBottom: 20 }}>
        <legend>Visibility</legend>
        <button onClick={show} style={{ marginRight: 8 }}>
          Show
        </button>
        <button onClick={hide} style={{ marginRight: 8 }}>
          Hide
        </button>
        <button onClick={() => setOpen(true)} style={{ marginRight: 8 }}>
          setOpen(true)
        </button>
        <button onClick={() => setOpen(false)}>setOpen(false)</button>
      </fieldset>

      <fieldset style={{ marginBottom: 20 }}>
        <legend>Metadata</legend>
        <button onClick={handleSetMetadata} style={{ marginRight: 8 }}>
          Set Metadata
        </button>
        <button onClick={handleUpdateMetadata}>Update Metadata</button>
      </fieldset>

      <fieldset style={{ marginBottom: 20 }}>
        <legend>Events</legend>
        <label htmlFor="event-select" style={{ display: 'block', marginBottom: 4 }}>
          Select Event:
        </label>
        <select
          id="event-select"
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value as ChatbotTypes.AppliedEventKey)}
          style={{ marginBottom: 8 }}
        >
          {Object.entries(eventOptions).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <div>
          <button onClick={handleSetupListeners} style={{ marginRight: 8 }}>
            Subscribe
          </button>
          <button
            onClick={handleUnsubscribe}
            disabled={subscriptionId === null}
            style={{ marginRight: 8 }}
          >
            Unsubscribe
          </button>
          <button onClick={handleFireEvent} style={{ marginRight: 8 }}>
            Fire Event
          </button>
          <button onClick={handleTrackEvent}>Track Event</button>
        </div>
      </fieldset>

      <section style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #ccc', padding: 12 }}>
        <h3>Logs</h3>
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          {messages.map((m, i) => (
            <li key={i} style={{ marginBottom: 4 }}>
              {m}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
